/*
 * Traffic light selector component
 */

import React from 'react';
import {castDataType, DataType, Mode} from '../common.jsx';
import Setting from './setting.jsx';
import Selection from './selection.jsx';
import Helpers from './helpers.jsx';
import DashboardIconSelect from '../../common/icon_select/DashboardIconSelect.lazy.jsx';
import CommonColorPicker from '../../common/ColorPicker/CommonColorPicker.jsx';

const FaIcons = [
  "fa fa-face-smile",
  "fa fa-face-smile-beam",
  "fa fa-face-laugh",
  "fa fa-face-laugh-beam",
  "fa fa-face-laugh-squint",
  "fa fa-face-laugh-wink",
  "fa fa-face-grin",
  "fa fa-face-grin-beam",
  "fa fa-face-grin-hearts",
  "fa fa-face-grin-stars",
  "fa fa-face-grin-wide",
  "fa fa-face-grin-wink",
  "fa fa-face-meh",
  "fa fa-face-meh-blank",
  "fa fa-face-flushed",
  "fa fa-face-surprise",
  "fa fa-face-grimace",
  "fa fa-face-rolling-eyes",
  "fa fa-face-tired",
  "fa fa-face-frown",
  "fa fa-face-frown-open",
  "fa fa-face-sad-cry",
  "fa fa-face-sad-tear",
  "fa fa-face-dizzy",
  "fa fa-face-angry",

  "fa fa-thumbs-up",
  "fa-regular fa-thumbs-up",
  "fa fa-thumbs-down",
  "fa-regular fa-thumbs-down",

  "fa fa-check",
  "fa-regular fa-circle-check",
  "fa fa-circle-check",
  "fa-solid fa-xmark",
  "fa-regular fa-circle-xmark",
  "fa fa-circle-xmark",
  "fa fa-ban",
];

export default class TrafficLightSelector extends Selection {
  static get TYPE() {
    return 'traffic_light_selector';
  }

  static icon() {
    return <i className="fa fa-traffic-light"></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'field_default_label',
      description: null,
      help: null,
    };
  }

  static defaultContent(language) {
    let translations = this.defaultContentTranslations(language);
    let options = [{value: '1', label: this.generateOptionTranslationKey()}];

    options.forEach((o, i) => {
      translations[language][o.label] = I18n.t('field_default_option', {scope: 'fe', locale: language});
    })
    return Object.assign(super.defaultContent(language), {
      label: 'label',
      description: 'description',
      name: 'field_' + Helpers.randomString(),
      reportKey: '',
      help: 'help',
      required: false,
      label_hidden: false,
      dataType: DataType.STRING,
      options: options,
      translations: translations
    });
  }

  readOptionsFromInputs(content, deletedOption) {
    jQuery(this.refs[this.settingsFormRef()]).find('.option-group').each((i, group) => {
      group = jQuery(group);
      group.find('.option').not(deletedOption).find('input').serializeArray().forEach((s) => {
        content[s.name] = s.value;
      })
    })
  }

  getOptions() {
    return this.props.content.options || [];
  }

  addOption(event) {
    let content = JSON.parse(JSON.stringify(this.props.content));
    let nextOptionIndex = this.getOptions().length;
    this.readOptionsFromInputs(content);

    [['value', this.getNextOptionValue()], ['label', this.constructor.generateOptionTranslationKey()], ['translated_label', '']].forEach((attr) => {
      content['options|' + nextOptionIndex + '|' + attr[0]] = attr[1];
    });

    this.beforeUpdateSettings(content);
    this.props.updateSettingsCallback(content.id, content, true);

    event.preventDefault();
  }

  deleteOption(event) {
    if (confirm(I18n.t('confirm_delete_option', {scope: 'fe'}))) {
      let content = JSON.parse(JSON.stringify(this.props.content))
      let deletedOption = jQuery(event.currentTarget).closest('.option');

      this.readOptionsFromInputs(content, deletedOption);
      this.beforeUpdateSettings(content);

      content.options = content.options.filter(o => o);
      this.props.updateSettingsCallback(content.id, content, true);
    }
    event.preventDefault();
  }

  renderView() {
    const {orientation, id} = this.props.content;
    const isVertical = (orientation === "vertical");

    return (
      <div
        style={{
          display: "flex",
          flexDirection: isVertical ? "column" : "row",
          flexWrap: "wrap",
          gap: 8
        }}
      >
        <label className={this.props.content.label_hidden ? 'hidden-label' : ''}>
          <span className="field-label">{this.getText('label')}</span>
          {this.props.content.required ? " *" : null}
          {this.helpIcon()}
        </label>
        {this.descriptionText()}

        {this.getOptions().map((opt, i) => {
          let answer =  this.getAnswer();
          let isActive = (answer === opt.value);
          let bg_color = `color-mix(in srgb, ${opt.bg_color || '#FFFFFF'}, black 20%)`;
          let text_color = `color-mix(in srgb, ${opt.text_color || '#000000'}, black 20%)`;
          return (
            <div
              key={`option-${i}-${opt.value}`}
              style={{
                display: "flex",
                flexDirection: isVertical ? "column" : "row",
                flexWrap: "wrap",
                gap: 8
              }}
            >
              <style>{`.traffic-light-item-${id}-${i}:hover{background-color: ${bg_color} !important; opacity: 1 !important}`}</style>
              <div
                onClick={() => this.updateAnswer((answer === opt.value) ? null : opt.value)}
                style={{
                  width: isVertical ? "fit-content" : "auto",
                  color: text_color,
                  backgroundColor: bg_color,
                  border: isActive ? "2px solid #000" : `2px solid ${bg_color}`,
                  gap: opt.hide_label ? 0 : 6,
                  cursor: this.props.mode === Mode.FILL ? "pointer" : "default",
                  padding: "6px 12px",
                  borderRadius: 20,
                  display: "flex",
                  alignItems: "center",
                  minHeight: "34px",
                  minWidth: "34px",
                  opacity: isActive ? 1 : 0.4,
                }}
                className={`traffic-light-item-${id}-${i}`}
              >
                {opt.display_icon === "left" && opt.icon && (
                  <i className={`${opt.icon} fa-xl`}/>
                )}

                <span>{!opt.hide_label && this.getText(opt.label)}</span>

                {opt.display_icon === "right" && opt.icon && (
                  <i className={`${opt.icon} fa-xl`}/>
                )}
              </div>

              {isActive && <input type="hidden" name={this.fullFieldName()} defaultValue={opt.value}/> }
            </div>
          )
        })}
      </div>
    );
  }

  renderSettings() {
    let opts = this.getOptions();
    let options = opts.map((o, oi) => {
      let valueFieldName = 'options|' + oi + '|value';
      let preventChangesOnSafeMode = !this.allowModifyInSafeMode && !(this.props.content.savedOptionsCount <= oi);

      return (
        <div key={o.label + '-' + opts.length} data-sort-order={oi} className="option" style={{position: 'relative'}}>
          <Helpers.SortHandle icon={'fa-solid fa-grip-vertical'}/>

          {!preventChangesOnSafeMode &&
            <div className="delete" style={{position: 'absolute', right: 0, top: 0}}>
              <a href="#" className="icon-trash" title={I18n.t('delete_option', {scope: 'fe'})} onClick={this.deleteOption}></a>
            </div>
          }

          <div className="row">
            <div className="col-xs-6">
              {preventChangesOnSafeMode &&
                <input type="hidden" name={valueFieldName} defaultValue={o.value}/>
              }
              <div className="form-group mb-1">
                <label>{I18n.t('value')}</label>
                <input
                  type="text"
                  name={valueFieldName}
                  defaultValue={o.value}
                  data-original-value={o.value}
                  onBlur={this.validateOptionValue}
                  onChange={this.updateSettings}
                  placeholder={I18n.t('option_value', {scope: 'fe'})}
                  className="form-control"
                  disabled={preventChangesOnSafeMode}
                  autoComplete={this.getAutoComplete()}
                />
              </div>
            </div>
            <div className="col-xs-6 pl-0">
              <input type="hidden" name={'options|' + oi + '|label'} defaultValue={o.label}/>
              {preventChangesOnSafeMode &&
                <input type="hidden" name={'options|' + oi + '|translated_label'} defaultValue={this.getText(o.label)}/>
              }
              <div className="form-group mb-1">
                <label>{I18n.t('label')}</label>
                <input
                  type="text"
                  name={'options|' + oi + '|translated_label'}
                  defaultValue={this.getText(o.label)}
                  onChange={this.updateSettings}
                  placeholder={I18n.t('option_label', {scope: 'fe'})}
                  className="form-control"
                  disabled={preventChangesOnSafeMode}
                  autoComplete={this.getAutoComplete()}
                />
                <Setting.Checkbox
                  name={'options|' + oi + '|hide_label'}
                  label={I18n.t('field_label_hide', {scope: 'fe'})}
                  value={o.hide_label}
                  onChange={this.updateSettings}
                  className="label-hidden-check pb-0 mb-0"
                />
              </div>
            </div>
            <div className="col-xs-6">
              <div className="form-group mb-1">
                <label>{I18n.t('background_color')}</label>
                <CommonColorPicker
                  name={'options|' + oi + '|bg_color'}
                  value={o.bg_color || '#FFFFFF'}
                  onChange={(v) => this.updateAdditionalSettings(oi, 'bg_color', v)}
                />
              </div>
            </div>
            <div className="col-xs-6 pl-0">
              <div className="form-group mb-1">
                <label>{I18n.t('text_color')}</label>
                <CommonColorPicker
                  name={'options|' + oi + '|text_color'}
                  value={o.text_color || '#000000'}
                  onChange={(v) => this.updateAdditionalSettings(oi, 'text_color', v)}
                />
              </div>
            </div>
            <div className="col-xs-12">
              <div className="form-group mb-1">
                <label>{I18n.t('display_icon')}</label>
                {['left', 'right', 'none'].map(v => (
                  <label key={v} style={{display: 'inline'}}>
                    <input
                      type="radio"
                      name={'options|' + oi + '|display_icon'}
                      value={v}
                      checked={o.display_icon === v}
                      data-original-value={o.display_icon}
                      onChange={this.updateSettings}
                      className="form-check-label"
                      style={{width: 'auto'}}
                      autoComplete={this.getAutoComplete()}
                    />
                    <span className="radio-inline-label" style={{display: 'inline'}}>{I18n.t(v)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-xs-12">
              <div className="form-group mb-1">
                <label>{I18n.t('icon')}</label>
                <DashboardIconSelect
                  name={'options|' + oi + '|icon'}
                  options={FaIcons}
                  getOptionLabel={(e) => <i className={`${e.label} fa-xl`}/>}
                  value={o.icon || 'none'}
                  onChange={(v) => this.updateAdditionalSettings(oi, 'icon', v)}
                  className='form-control'
                />
              </div>
            </div>
          </div>
          <hr className="mt-0 mb-1" style={{borderColor: '#ccc'}}/>
        </div>
      )
    })

    return (
      <div ref="settings">
        <Setting.Checkbox
          name="required"
          label={I18n.t('field_required', {scope: 'fe'})}
          value={this.props.content.required}
          onChange={this.updateSettings}
        />
        <Setting.TextField
          name="label"
          label={I18n.t('field_label', {scope: 'fe'})}
          value={this.getText('label')}
          onChange={this.updateSettings}
        />
        <Setting.Checkbox
          name="label_hidden"
          className="label-hidden-check"
          label={I18n.t('field_label_hide', {scope: 'fe'})}
          value={this.props.content.label_hidden}
          onChange={this.updateSettings}
        />
        {!this.props.settings['hideDescriptionField'] &&
          <Setting.LongTextField
            name="description"
            label={I18n.t('field_description', {scope: 'fe'})}
            value={this.getText('description')}
            onChange={this.updateSettings}
          />
        }
        {!this.props.settings['hideHelpTextField'] &&
          <Setting.LongTextField
            name="help"
            label={I18n.t('field_help', {scope: 'fe'})}
            value={this.getText('help')}
            onChange={this.updateSettings}
          />
        }
        <Setting.Dropdown
          name="orientation"
          label={I18n.t('orientation')}
          value={this.props.content.orientation}
          onChange={this.updateSettings}
          options={['horizontal', 'vertical'].map(v => ([v, I18n.t(v)]))}
          disabled={!this.allowModifyInSafeMode}
        />

        <div className="setting">
          <label>{I18n.t('field_options', {scope: 'fe'})}</label>
          <div className="option-groups">
            <div className="option-group">
              <div className="options">
                {options}
              </div>
              <div className="add-buttons">
                <a href="#" className="btn btn-primary" onClick={this.addOption}>
                  <i className="icon-add"></i> {I18n.t('add_option', {scope: 'fe'})}
                </a>
              </div>
            </div>
          </div>
        </div>

        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
      </div>
    );
  }

  updateOptionOrder(event) {
    let sortContainer = jQuery(event.target);
    let oldOptions = this.getOptions();
    let newOptions = [];
    sortContainer.sortable('toArray', {attribute: 'data-sort-order'}).forEach((i) => {
      newOptions.push(Object.assign({}, oldOptions[parseInt(i, 10)]));
    });

    let content = {id: this.props.content.id, options: newOptions};
    this.props.updateSettingsCallback(content.id, content, true);
  }

  updateAdditionalSettings(idx, attr, value) {
    let options = this.getOptions();
    options[idx][attr] = value;
    let content = {id: this.props.content.id, options: options};
    this.props.updateSettingsCallback(content.id, content, true);
  }

  beforeUpdateSettings(content) {
    content.options = [];

    // Delete old option translations
    content.translations[this.props.language] = content.translations[this.props.language] || {};
    Object.keys(content.translations[this.props.language]).forEach((k) => {
      if (k.match(/^opt\-/)) {
        delete content.translations[this.props.language][k];
      }
    })

    Object.keys(content).filter((k) => {
      return k.match(/^options\|/);
    }).forEach((k) => {
      let parts = k.split('|'); // options|index|attr
      if (parts[0] === 'options') {
        let oIndex = parseInt(parts[1], 10);
        if (parts[2] === 'translated_label') {
          content.translations[this.props.language][content[k.replace(/translated_label$/, 'label')] || content.options[oIndex].label] = content[k];
        } else {
          content.options[oIndex] = content.options[oIndex] || {};
          content.options[oIndex][parts[2]] = (parts[2] === 'hide_label') ? castDataType(content[k], DataType.BOOLEAN) : content[k];
        }
      }
      delete content[k];
    });

    content.required = castDataType(content.required, DataType.BOOLEAN);
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
  }
}
